/**
 * @congress/platform-helpers v3.0.0
 * @version 3.0.0
 * @license CYIM: www.cyim.com
 */

import VueApollo from 'vue-apollo';
import { InMemoryCache, HttpLink, ApolloLink, ApolloClient, from } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import WebSocketLink from './WebSocketLink.js';
import { CachePersistor } from 'apollo-cache-persist';
import AuthHelper from '@congress/auth-helper';
import Configuration from '../common/config.js';
class GraphqlHelper {
  /**
   * Return instance of GraphqlHelper or create it
   * @example
   * import GraphqlHelper from "@congress/platform-helpers/GraphqlHelper";
   * const graphQl = GraphqlHelper.getInstance()
   * @returns {GraphqlHelper} - Instance of GraphqlHelper
   */
  static getInstance() {
    if (!window.GraphqlHelper) {
      window.GraphqlHelper = new GraphqlHelper();
    }
    return window.GraphqlHelper;
  }

  /**
   * @constructor GraphqlHelper
   * @throws {"GraphQlHelper is already initialized ! You can't use this constructor directly. Please use getInstance instead"}
   */
  constructor() {
    if (window.GraphqlHelper) {
      throw "GraphQlHelper is already initialized ! You can't use this constructor directly. Please use getInstance instead";
    }
    this.apolloClient = null;
    this.otherApolloClients = null;
    this.vueApolloProvider = null;
    this.wsLink = null;
  }

  /**
   * Use this function if you want to update previous initialisation (in constructor)
   * @example
   * GraphqlHelper.getInstance().setAuth(AuthHelper.AuthType.KEYCLOAK, { realm: ... });
   * @param {AuthHelper.AuthType} type=AuthHelper.AuthType.KEYCLOAK - Authentication type
   * @param options - AuthHelper options
   */
  setAuth() {
    let type = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : AuthHelper.AuthType.KEYCLOAK;
    let options = arguments.length > 1 ? arguments[1] : undefined;
    window.AuthHelper = new AuthHelper(type, options);
  }

  /**
   * DEPRECATED: The base64 encoded token from auth that can be sent in the Authorization header in requests to services. *use `window.AuthHelper.getToken()` instead*
   * @deprecated use window.AuthHelper.getToken() instead
   * @returns {string}
   */
  async getToken() {
    return await window?.AuthHelper?.getToken();
  }

  /**
   * DEPRECATED: The parsed token as a JavaScript object from auth. *use `window.AuthHelper.getTokenParsed()` instead*
   * @deprecated use window.AuthHelper.getTokenParsed() instead
   * @returns {Keycloak.KeycloakTokenParsed|Object}
   */
  async getTokenParsed() {
    return await window?.AuthHelper?.getTokenParsed();
  }

  /**
   * Initialize cache
   * @returns {Promise<void>}
   * @private
   */
  async _setCache() {
    this._cache = new InMemoryCache();
    try {
      this.cachePersistor = new CachePersistor({
        cache: this._cache,
        storage: window.sessionStorage
      });
      await this.cachePersistor.restore();
    } catch (e) {
      this.cachePersistor = null;
    }
  }

  /**
   * Get token from AuthHelper
   * @returns {Promise<string>}
   * @private
   */
  async _getAuthToken() {
    const token = await window?.AuthHelper?.getToken();
    return token || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjbGllbnRJZCI6Ijg1MzI2M2EyLTQyNDItNDlmOC04NTJhLThjMTI1M2U3ZDA4NiIsInBlcm1pc3Npb25zIjpbInJlYWRBbGwiLCJ3cml0ZUFsbCJdLCJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkN5aW0iLCJpYXQiOjE1MTYyMzkwMjJ9.AxOYR_dzjD1SwPMpj5V3u7zHmkCY0nrWEOdsiLFXhz0";
  }
  async getHeaders() {
    const headers = {};
    const jwt = await this._getAuthToken();
    if (jwt) {
      headers["Authorization"] = `Bearer ${jwt}`;
    }
    headers['Accept'] = 'multipart/mixed; deferSpec=20220824, application/json';
    headers["Content-Type"] = "application/json";
    return headers;
  }
  async _setupProvider(httpEndpoint, options, wsEndpoint) {
    let hasWsEndpoint = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    await this._setCache();
    const httpLink = httpEndpoint ? new HttpLink({
      uri: httpEndpoint,
      fetch: async (uri, options) => {
        // where we inject the headers
        options.headers = await this.getHeaders();
        return fetch(uri, options);
      }
    }) : undefined;
    // Add auth for HTTP & WS
    const authLink = setContext(async (_, _ref) => {
      let {
        headers
      } = _ref;
      const token = await this._getAuthToken();
      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : ""
        }
      };
    });
    // Create the subscription websocket link
    if (wsEndpoint) {
      if (window?.GraphqlHelper?.graphQlWS) {
        this.wsLink = window.GraphqlHelper.graphQlWS;
      } else {
        // TODO: find a better way to check this
        // wsEndpoint instanceof WebSocketLink -> doesn't work with compilation
        if (typeof wsEndpoint === "object") {
          this.wsLink = wsEndpoint;
        } else {
          this.wsLink = new WebSocketLink({
            url: wsEndpoint,
            // Options available: https://github.com/apollographql/subscriptions-transport-ws/blob/master/src/client.ts#L61-L71
            options: {
              // Reconnect in case client disconnects and connects again
              reconnect: true
            },
            connectionParams: async () => {
              // simulate an auth token sent from the client over the WS connection
              // const token = "some-token";
              const token = await this._getAuthToken();
              return {
                ...(token && {
                  token
                })
              };
            }
          });
        }
        window.GraphqlHelper.graphQlWS = this.wsLink;
      }
    }
    // using the ability to ApolloLink.split links, you can send data to each link
    // depending on what kind of operation is being sent
    let link;
    if (httpLink && this.wsLink && hasWsEndpoint) {
      link = ApolloLink.split(
      // ApolloLink.split based on operation type
      _ref2 => {
        let {
          query
        } = _ref2;
        const definition = getMainDefinition(query);
        return definition.kind === "OperationDefinition" && definition.operation === "subscription";
      }, this.wsLink, httpLink);
    } else if (httpLink && (!this.wsLink || !hasWsEndpoint)) {
      link = httpLink;
    } else if (!httpLink && this.wsLink) {
      link = this.wsLink;
    }

    // Log errors
    const errorLink = onError(_ref3 => {
      let {
        graphQLErrors,
        networkError
      } = _ref3;
      if (graphQLErrors) {
        graphQLErrors.forEach(_ref4 => {
          let {
            message,
            locations,
            path
          } = _ref4;
          return console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
        });
      }
      if (networkError) {
        console.log(`[Network error]: ${networkError}`);
      }
    });
    return new ApolloClient({
      link: from([authLink, errorLink, link]),
      cache: this._cache,
      ...options
    });
  }

  /**
   * Get Apollo provider or create it (ONLY FOR VUE)
   * @param {string} [httpEndpoint=process.env.VUE_APP_GRAPHQL_HTTP]
   * @param {Object} [options={}] Apollo client options
   * @param {string|WebSocketLink} [wsEndpoint=null]
   * @param {Array} options for secondary apollo clients - No Subscription
   * @returns {Promise<VueApollo|{apolloProvider: VueApollo, cachePersistor: (null|CachePersistor<unknown>|*)}>}
   */
  async getProvider() {
    let httpEndpoint = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Configuration.value("VUE_APP_GRAPHQL_HTTP");
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let wsEndpoint = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    let otherApolloClientOptions = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
    if (this.vueApolloProvider) {
      return {
        apolloProvider: this.vueApolloProvider,
        cachePersistor: this.cachePersistor
      };
    } else {
      if (!this.apolloClient) {
        this.apolloClient = await this._setupProvider(httpEndpoint, options, wsEndpoint, true);
      }
      if (!this.otherApolloClients && otherApolloClientOptions?.length > 0) {
        this.otherApolloClients = {};
        for (const [index, opt] of otherApolloClientOptions.entries()) {
          let clientId = opt.clientId ?? index;
          this.otherApolloClients[clientId] = await this._setupProvider(opt.httpEndpoint, opt.options);
        }
      }
      // Create vue apollo provider
      this.vueApolloProvider = new VueApollo({
        defaultClient: this.apolloClient,
        // TODO: Check this (apolloClient != vueApolloProvider ???)
        clients: this.otherApolloClients ? {
          default: this.apolloClient,
          ...this.otherApolloClients
        } : {},
        defaultOptions: {
          $query: {
            fetchPolicy: "cache-first"
          }
        },
        errorHandler(error) {
          // eslint-disable-next-line no-console
          console.log("%cError", "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;", error.message);
        }
      });
      return {
        apolloProvider: this.vueApolloProvider,
        cachePersistor: this.cachePersistor
      };
    }
  }
  /**
   * Get Apollo client or create it
   * @param {string} [httpEndpoint=process.env.VUE_APP_GRAPHQL_HTTP]
   * @param {Object} [options={}] Apollo client options
   * @param {string|WebSocketLink} [wsEndpoint=null]
   * @returns {Promise<ApolloClient>}
   */
  async getApolloClient() {
    let httpEndpoint = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Configuration.value("VUE_APP_GRAPHQL_HTTP");
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let wsEndpoint = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    if (this.apolloClient) {
      return this.apolloClient;
    } else {
      await this._setupProvider(httpEndpoint, options, wsEndpoint);
      return this.apolloClient;
    }
  }
}
export { GraphqlHelper as default };
//# sourceMappingURL=index.js.map
